// UnifiedNode.jsx - Componente unificato per Team e Phase
import React, { useMemo, memo } from 'react';

const Node = ({
  node,
  selected,
  onSelect,
  onDoubleClick,
  onDelete,
  onStartConnection,
  onEndConnection,
  onHover,
  onLeave,
  connecting,
  incomingConnectionsCount = 0, // Numero di connessioni in ingresso
  gruppoFase = null // { colore, etichetta } se il nodo appartiene a un gruppo-fase
}) => {
  const isTeam = node.type === 'team';
  const isPhase = node.type === 'phase';
  const isGironeOrClassifica = node.phaseType === 'Girone' || node.phaseType === 'Classifica';

  // Configurazione basata sul tipo
  const nodeConfig = {
    team: {
      fill: '#6C63FF',
      stroke: selected ? '#1976D2' : '#4A4A4A',
      showInputConnection: false,
      showOutputConnection: true,
      icon: 'T',
      iconBg: '#FFF',
      iconColor: '#6C63FF'
    },
    phase: {
      fill: node.phaseType === 'Girone' ? '#4CAF50' :
            node.phaseType === 'Classifica' ? '#9C27B0' : '#FF9800',
      stroke: selected ? '#1976D2' : '#666',
      showInputConnection: true,
      showOutputConnection: true,
      icon: null
    }
  };

  const config = nodeConfig[node.type] || nodeConfig.phase;

  // Calcola il numero di porte di output per Girone/Classifica
  // Uguale al numero di squadre in ingresso (connessioni entranti)
  const outputPortCount = useMemo(() => {
    if (!isGironeOrClassifica) return 1;
    return Math.max(1, incomingConnectionsCount);
  }, [isGironeOrClassifica, incomingConnectionsCount]);

  // Calcola le posizioni delle porte di output
  const outputPortPositions = useMemo(() => {
    if (!isGironeOrClassifica || outputPortCount <= 1) {
      return [{ y: node.y + node.height / 2, position: 1 }];
    }

    const positions = [];
    const padding = 10; // Padding dall'alto e dal basso
    const availableHeight = node.height - padding * 2;
    const spacing = availableHeight / (outputPortCount - 1 || 1);

    for (let i = 0; i < outputPortCount; i++) {
      positions.push({
        y: node.y + padding + (spacing * i),
        position: i + 1 // Posizione 1-based (1°, 2°, 3°, ecc.)
      });
    }
    return positions;
  }, [isGironeOrClassifica, outputPortCount, node.y, node.height]);

  const handleMouseDown = (e) => {
    // Solo il tasto sinistro seleziona e trascina: il centrale deve arrivare al canvas, che con
    // quello fa il pan anche partendo da sopra un nodo.
    if (e.button !== 0) return;
    e.stopPropagation();
    onSelect?.(node, e);
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    onDoubleClick?.(node);
  };

  // Handler per click sulla porta di input
  // Passa indice 0 perché c'è una sola porta di input per i nodi normali
  const handleInputConnectionClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (connecting) {
      // Passa 0 come indice della porta di input (singola porta = indice 0)
      onEndConnection?.(node.id, 0);
    } else {
      onStartConnection?.(node.id, null); // null = porta di input (non ha posizione)
    }
  };

  // Handler per click sulla porta di output con posizione specifica
  const handleOutputConnectionClick = (e, position = 1) => {
    e.stopPropagation();
    e.preventDefault();

    if (connecting) {
      // Quando si termina su una porta di output, passa l'indice (position-1 per 0-based)
      onEndConnection?.(node.id, position - 1);
    } else {
      // Passa la posizione della porta di output (1-based)
      onStartConnection?.(node.id, position);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(node.id);
  };

  const handleMouseEnter = () => {
    onHover?.(node.id);
  };

  const handleMouseLeave = () => {
    onLeave?.();
  };

  return (
    <g 
      className={`unified-node ${node.type} ${selected ? 'selected' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Nodo principale */}
      <rect
        x={node.x}
        y={node.y}
        width={node.width}
        height={node.height}
        fill={config.fill}
        stroke={config.stroke}
        strokeWidth={selected ? 3 : 1}
        rx={isTeam ? 5 : 8}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        style={{ cursor: 'grab' }}
      />

      {/* Appartenenza a un gruppo-fase dichiarato a mano: cornice e sigla del gruppo */}
      {gruppoFase && (
        <>
          <rect
            x={node.x - 4}
            y={node.y - 4}
            width={node.width + 8}
            height={node.height + 8}
            fill="none"
            stroke={gruppoFase.colore}
            strokeWidth="2"
            strokeDasharray="5 3"
            rx={isTeam ? 8 : 11}
            pointerEvents="none"
          />
          <rect
            x={node.x + node.width - 18}
            y={node.y - 11}
            width="22"
            height="14"
            fill={gruppoFase.colore}
            rx="3"
            pointerEvents="none"
          />
          <text
            x={node.x + node.width - 7}
            y={node.y - 1}
            textAnchor="middle"
            fill="white"
            fontSize="9"
            fontWeight="bold"
            pointerEvents="none"
          >
            {gruppoFase.etichetta}
          </text>
        </>
      )}

      {/* Contenuto specifico per team */}
      {isTeam && (
        <>
          <circle
            cx={node.x + 15}
            cy={node.y + node.height / 2}
            r="8"
            fill={config.iconBg}
            pointerEvents="none"
          />
          <text
            x={node.x + 15}
            y={node.y + node.height / 2 + 3}
            textAnchor="middle"
            fill={config.iconColor}
            fontSize="10"
            fontWeight="bold"
            pointerEvents="none"
          >
            {config.icon}
          </text>
          <text
            x={node.x + 35}
            y={node.y + node.height / 2 + 3}
            fill="white"
            fontSize="11"
            fontWeight="bold"
            pointerEvents="none"
          >
            {node.name}
          </text>
        </>
      )}
      
      {/* Contenuto specifico per fase */}
      {isPhase && (
        <>
          <text
            x={node.x + node.width / 2}
            y={node.y + 20}
            textAnchor="middle"
            fill="white"
            fontSize="12"
            fontWeight="bold"
            pointerEvents="none"
          >
            {node.phaseType}
          </text>
          <text
            x={node.x + node.width / 2}
            y={node.y + 40}
            textAnchor="middle"
            fill="white"
            fontSize="10"
            pointerEvents="none"
          >
            {node.name}
          </text>
          <text
            x={node.x + node.width / 2}
            y={node.y + 68}
            textAnchor="middle"
            fill="rgba(255,255,255,0.7)"
            fontSize="8"
            pointerEvents="none"
          >
            ID: {node.originalId}
          </text>
        </>
      )}

      {/* Hint per doppio click quando selezionato */}
      {selected && (
        <text
          x={node.x + node.width / 2}
          y={node.y + node.height + 15}
          textAnchor="middle"
          fill="#666"
          fontSize="8"
          pointerEvents="none"
        >
          Doppio click per modificare
        </text>
      )}

      {/* Punto di connessione input (solo per fasi) */}
      {config.showInputConnection && (
        <circle
          cx={node.x - 8}
          cy={node.y + node.height / 2}
          r="6"
          fill="#FFF"
          stroke={config.fill}
          strokeWidth="2"
          onClick={handleInputConnectionClick}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ cursor: 'crosshair' }}
        />
      )}

      {/* Porte di output - multiple per Girone/Classifica, singola per altri */}
      {config.showOutputConnection && (
        isGironeOrClassifica && outputPortCount > 1 ? (
          // Porte multiple per Girone/Classifica
          outputPortPositions.map((port, index) => (
            <g key={`output-${index}`}>
              {/* Etichetta posizione */}
              <text
                x={node.x + node.width + 18}
                y={port.y + 4}
                fill="#666"
                fontSize="10"
                fontWeight="bold"
                pointerEvents="none"
              >
                {port.position}°
              </text>
              {/* Porta */}
              <circle
                cx={node.x + node.width + 8}
                cy={port.y}
                r="6"
                fill="#FFF"
                stroke={config.fill}
                strokeWidth="2"
                onClick={(e) => handleOutputConnectionClick(e, port.position)}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ cursor: 'crosshair' }}
              />
            </g>
          ))
        ) : (
          // Singola porta di output per team e altri tipi
          <circle
            cx={node.x + node.width + 8}
            cy={node.y + node.height / 2}
            r="6"
            fill="#FFF"
            stroke={config.fill}
            strokeWidth="2"
            onClick={(e) => handleOutputConnectionClick(e, 1)}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ cursor: 'crosshair' }}
          />
        )
      )}
      
      {/* Indicatore di connessione attiva */}
      {connecting === node.id && (
        <circle
          cx={node.x + node.width / 2}
          cy={node.y + node.height / 2}
          r={Math.max(node.width, node.height) / 2 + 10}
          fill="none"
          stroke="#2196F3"
          strokeWidth="2"
          strokeDasharray="5,5"
          className="connecting-indicator"
        />
      )}
    </g>
  );
};

// Memoizzato: senza, i 96 nodi della formula del 98 si ridisegnavano tutti a ogni mousemove.
export default memo(Node);